import React, { useState, useMemo } from "react";
import { Button, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "../styles/testSearchResults.css";

const THRESHOLD_FIELDS = [
    { label: "Right 500 Hz", field: "right_threshold_500" },
    { label: "Right 1000 Hz", field: "right_threshold_1000" },
    { label: "Right 2000 Hz", field: "right_threshold_2000" },
    { label: "Right 4000 Hz", field: "right_threshold_4000" },
    { label: "Right 6000 Hz", field: "right_threshold_6000" },
    { label: "Right 8000 Hz", field: "right_threshold_8000" },
    { label: "Left 500 Hz", field: "left_threshold_500" },
    { label: "Left 1000 Hz", field: "left_threshold_1000" },
    { label: "Left 2000 Hz", field: "left_threshold_2000" },
    { label: "Left 4000 Hz", field: "left_threshold_4000" },
    { label: "Left 6000 Hz", field: "left_threshold_6000" },
    { label: "Left 8000 Hz", field: "left_threshold_8000" },
];

const TestSearchResults = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const results = useMemo(() => location.state?.results ?? [], [location.state?.results]);

    const [dateSortDir, setDateSortDir] = useState(null);

    const [thresholdField, setThresholdField] = useState("");
    const [thresholdSortDir, setThresholdSortDir] = useState("asc");

    // Check if any result is diagnostic
    const hasDiagnostic = useMemo(
        () => results.some((r) => r.redcap_event_name?.includes("diag")),
        [results]
    );

    const patientName = (r) => `${r.first_name} ${r.last_name}`;

    const patientCity = (r) => String(r.city);

    const patientState = (r) => String(r.state);

    const testTypeLabel = (r) => {
        if (r.redcap_event_name.includes("screen")) return "Screening";
        if (r.redcap_event_name.includes("diag")) return "Diagnostic";
        return String(r.redcap_event_name);
    };

    const isScreening = (r) => {
        return r.redcap_event_name.includes("screen");
    };

    const dateTaken = (r) => {
        const raw =
            r.dateTaken ??
            r.testDate ??
            r.test_date ??
            r.date_taken ??
            r.visit_date ??
            r.created_at ??
            "";

        if (!raw) return "";

        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);

        return String(raw);
    };

    const sortedResults = useMemo(() => {
        // Threshold sort takes priority if a field is selected
        if (thresholdField) {
            return [...results].sort((a, b) => {
                const va = Number(a[thresholdField] ?? Infinity);
                const vb = Number(b[thresholdField] ?? Infinity);
                return thresholdSortDir === "asc" ? va - vb : vb - va;
            });
        }

        // Otherwise go back to date sort
        if (dateSortDir) {
            return [...results].sort((a, b) => {
                const da = new Date(dateTaken(a) || 0).getTime();
                const db = new Date(dateTaken(b) || 0).getTime();
                return dateSortDir === "asc" ? da - db : db - da;
            });
        }

        return results;
    }, [results, dateSortDir, thresholdField, thresholdSortDir]);

    const handleRowClick = (row) => {
        const patient = {
            first_name: String(patientName(row)).split(" ")[0] || "Unknown",
            last_name: String(patientName(row)).split(" ").slice(1).join(" ") || "",
            patientId: row.patientId ?? row.patient_id ?? null,
        };

        navigate("/individualPatientTest", {
            state: {
                test: row,
                isScreening: isScreening(row),
                patient,
            },
        });
    };

    return (
        <div className="ts-results">
            <h2 className="ts-results__title">Test Search Results</h2>

            <div className="ts-results__toolbar">
                <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />} variant="text">
                    Back to Search
                </Button>
                {hasDiagnostic && (
                    <div className="ts-results__thresholdSort">
                        <TextField
                            select
                            size="small"
                            label="Sort by threshold"
                            value={thresholdField}
                            onChange={(e) => {
                                setThresholdField(e.target.value);
                                setDateSortDir(null); // clear date sort
                            }}
                            className="ts-results__thresholdField"
                        >
                            <MenuItem value="">None</MenuItem>
                            {THRESHOLD_FIELDS.map(({ label, field }) => (
                                <MenuItem key={field} value={field}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>

                        {thresholdField && (
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={thresholdSortDir}
                                onChange={(_, val) => { if (val) setThresholdSortDir(val); }}
                            >
                                <ToggleButton value="asc">▲ Ascending</ToggleButton>
                                <ToggleButton value="desc">▼ Descending</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </div>
                )}
            </div>

            <div className="ts-results__tableWrap">
                <table className="ts-results__table">
                    <thead>
                        <tr>
                            <th className="ts-results__th">Patient Name</th>
                            <th className="ts-results__th">City</th>
                            <th className="ts-results__th">State</th>
                            <th className="ts-results__th">Test Type</th>
                            <th
                                className="ts-results__th ts-results__th--sortable"
                                onClick={() => {
                                    setThresholdField(""); // clear threshold sort
                                    setDateSortDir((prev) =>
                                        prev === null ? "asc" : prev === "asc" ? "desc" : null
                                    );
                                }}
                            >
                                Date Taken{" "}
                                <span className="ts-results__sortIcon">
                                    {dateSortDir === "asc" ? "▲" : dateSortDir === "desc" ? "▼" : "⇅"}
                                </span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {sortedResults.length === 0 ? (
                            <tr>
                                <td className="ts-results__emptyCell" colSpan={5}>
                                    No results found
                                </td>
                            </tr>
                        ) : (
                            sortedResults.map((row, idx) => (
                                <tr
                                    key={row.id ?? idx}
                                    className="ts-results__row"
                                    onClick={() => handleRowClick(row)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") handleRowClick(row);
                                    }}
                                >
                                    <td className="ts-results__td" title={patientName(row)}>
                                        {patientName(row)}
                                    </td>
                                    <td className="ts-results__td" title={patientCity(row)}>
                                        {patientCity(row)}
                                    </td>
                                    <td className="ts-results__td" title={patientState(row)}>
                                        {patientState(row)}
                                    </td>
                                    <td className="ts-results__td" title={testTypeLabel(row)}>
                                        {testTypeLabel(row)}
                                    </td>
                                    <td className="ts-results__td" title={dateTaken(row)}>
                                        {dateTaken(row)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TestSearchResults;